// ══════════════════════════════════════════════════════════════════════════════
// BOUTON TRADUIRE - ABRACADATCHAT
// Traduction de messages avec option "Toujours traduire [langue]"
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Languages, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Drapeaux des langues
const FLAGS = {
  fr: '🇫🇷',
  en: '🇬🇧',
  es: '🇪🇸',
  de: '🇩🇪',
  it: '🇮🇹'
};

const LANG_NAMES = {
  fr: 'français',
  en: 'anglais',
  es: 'espagnol',
  de: 'allemand',
  it: 'italien'
};

export default function TranslateButton({ 
  text, 
  messageId,
  userLang = 'fr',
  onTranslated,
  autoTranslateLanguages = [],
  onAutoTranslateChange
}) {
  const [loading, setLoading] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [detectedLang, setDetectedLang] = useState(null);
  
  const translateMessage = async () => {
    if (loading) return;
    
    setLoading(true);
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/translate/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: text,
          target_lang: userLang
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTranslation(data.translated_text);
        setDetectedLang(data.detected_lang || data.source_lang);
        
        if (onTranslated) {
          onTranslated(messageId, data);
        }
      } else {
        toast.error('Erreur de traduction');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Impossible de traduire');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleAutoTranslate = async () => {
    if (!detectedLang || detectedLang === userLang) return;
    
    const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
    const isCurrentlyEnabled = autoTranslateLanguages.includes(detectedLang);
    
    try {
      const response = await fetch(`${API_URL}/api/translate/preferences/auto-translate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          language: detectedLang,
          enabled: !isCurrentlyEnabled
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        if (onAutoTranslateChange) {
          onAutoTranslateChange(data.auto_translate_from);
        }
      }
    } catch (error) {
      console.error('Auto-translate toggle error:', error);
    }
  };
  
  // Si pas encore traduit, afficher le bouton "Traduire"
  if (!translation) {
    return (
      <button
        onClick={translateMessage}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-orange-500 transition-colors mt-1"
        data-testid="translate-btn"
      >
        {loading ? (
          <>
            <Loader2 size={12} className="animate-spin" />
            <span>Traduction...</span>
          </>
        ) : (
          <>
            <Languages size={12} />
            <span>Traduire</span>
          </>
        )}
      </button>
    );
  }
  
  // Afficher la traduction
  const isAutoEnabled = autoTranslateLanguages.includes(detectedLang);
  
  return (
    <div className="mt-2 space-y-2">
      {/* Ligne de séparation avec drapeau */}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <div className="flex-1 h-px bg-slate-200" />
        <span>{FLAGS[detectedLang] || '🌐'} → {FLAGS[userLang]}</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>
      
      {/* Texte traduit ou original */}
      <div className="text-sm">
        {showOriginal ? (
          <p className="text-slate-500 italic">{text}</p>
        ) : (
          <p className="text-slate-700">{translation}</p>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-3 text-xs">
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          {showOriginal ? 'Voir traduction' : 'Voir original'}
        </button>
        
        {detectedLang && detectedLang !== userLang && (
          <button
            onClick={toggleAutoTranslate}
            className={`flex items-center gap-1 transition-colors ${
              isAutoEnabled 
                ? 'text-green-600 hover:text-green-700' 
                : 'text-slate-400 hover:text-orange-500'
            }`}
            data-testid="auto-translate-toggle"
          >
            {isAutoEnabled && <Check size={12} />}
            <span>
              {isAutoEnabled 
                ? `Traduction auto ${LANG_NAMES[detectedLang]} activée`
                : `Toujours traduire ${LANG_NAMES[detectedLang]}`
              }
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
